/**
 * Day-by-day telemetry for ONE robot, built from its finished navigation runs.
 * Portable by contract: the feature is this component plus its hook, it takes
 * the robot id as a prop, and it knows nothing about the page around it.
 *
 * Sized to read without scrolling: the day strip on top is the picker, the
 * selected day (today by default) sits right under it as five numbers and a
 * line of detail, and everything else is folded away until asked for.
 */
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { Card, Icon, SectionHead } from "../../components/ui";
import DayBars from "./DayBars";
import DayTable from "./DayTable";
import DaySummary, { NAV_HEADLINES, StatRow } from "./DaySummary";
import useRobotTelemetry from "./useRobotTelemetry";
import { RANGE_OPTIONS, dayTitle, formatDay, timeZoneLabel } from "./robotTelemetry";
import "./robotTelemetry.css";

const BANNER_TONES = {
  error: { bg: "var(--bq-dangerSoft)", fg: "var(--bq-danger)", icon: "circle-alert" },
  offline: { bg: "var(--bq-warningSoft)", fg: "var(--bq-warning)", icon: "wifi-off" },
  unavailable: { bg: "var(--bq-surface2)", fg: "var(--bq-text2)", icon: "info" },
};

const Banner = ({ error, onRetry }) => {
  const tone = BANNER_TONES[error.kind] || BANNER_TONES.error;
  return (
    <div
      role="alert"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "9px 11px",
        background: tone.bg,
        borderRadius: 8,
        fontSize: 12.5,
        color: tone.fg,
      }}
    >
      <Icon name={tone.icon} size={15} />
      <span style={{ flex: 1, minWidth: 0 }}>{error.message}</span>
      {onRetry && error.kind !== "unavailable" && (
        <button type="button" className="rt-link" onClick={onRetry} style={{ color: "inherit" }}>
          Try again
        </button>
      )}
    </div>
  );
};

Banner.propTypes = {
  error: PropTypes.shape({ message: PropTypes.string, kind: PropTypes.string }).isRequired,
  onRetry: PropTypes.func,
};

const RangePicker = ({ value, onChange }) => (
  <fieldset className="rt-segments">
    <legend className="rt-sr">Days shown</legend>
    {RANGE_OPTIONS.map((n) => (
      <button
        key={n}
        type="button"
        className="rt-seg"
        aria-pressed={n === value}
        onClick={() => onChange(n)}
      >
        {n} days
      </button>
    ))}
  </fieldset>
);

RangePicker.propTypes = { value: PropTypes.number.isRequired, onChange: PropTypes.func.isRequired };

const Skeleton = ({ height }) => (
  <div className="rt-skeleton" style={{ height }} aria-hidden="true" />
);

Skeleton.propTypes = { height: PropTypes.number.isRequired };

/** A folded section: the summary line is the toggle, the body opens beneath it. */
const Fold = ({ label, children }) => (
  <details className="rt-fold">
    <summary>
      <Icon name="chevron-right" size={14} className="rt-fold-icon" />
      {label}
    </summary>
    <div style={{ padding: "4px 0 14px" }}>{children}</div>
  </details>
);

Fold.propTypes = { label: PropTypes.string.isRequired, children: PropTypes.node };

const RobotTelemetryPanel = ({
  robotId,
  enabled = true,
  showNavigation = false,
  heading = "Telemetry",
  dayRunsHref,
}) => {
  const t = useRobotTelemetry(robotId, { enabled });

  const loaded = t.days.length > 0;
  const rangeIsEmpty = loaded && t.days.every((d) => !d.runs);
  const title = dayTitle(t.day, t.today);
  const dateNote = title === formatDay(t.day) ? null : formatDay(t.day);
  const hasRuns = t.selected?.runs > 0;

  const renderSummary = () => {
    if (t.loading && !loaded) return <Skeleton height={64} />;
    if (rangeIsEmpty) {
      return (
        <p className="rt-empty">
          No runs in the last {t.range} days. Runs show up here once the robot finishes one and
          reports it.
        </p>
      );
    }
    if (!t.selected) return null;
    if (!hasRuns) {
      return (
        <p className="rt-empty">
          {t.day === t.today ? "No runs yet today." : `No runs on ${formatDay(t.day)}.`} Pick a day
          with a bar to see its numbers.
        </p>
      );
    }
    return <DaySummary day={t.selected} days={t.days} />;
  };

  return (
    <Card>
      <SectionHead
        icon="chart-column"
        title={heading}
        sub={timeZoneLabel(t.tz)}
        right={<RangePicker value={t.range} onChange={t.setRange} />}
      />

      <div className="rt-body">
        {t.error && <Banner error={t.error} onRetry={t.reload} />}

        {t.loading && !loaded ? (
          <Skeleton height={118} />
        ) : (
          loaded && (
            <DayBars days={t.days} selected={t.day} today={t.today} onSelect={t.selectDay} />
          )
        )}

        <section aria-labelledby="rt-day-title" className="rt-day">
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 12 }}>
            <h3 id="rt-day-title" className="rt-day-title" aria-live="polite">
              {title}
            </h3>
            {dateNote && (
              <span style={{ fontSize: 12.5, color: "var(--bq-text3)" }}>{dateNote}</span>
            )}
          </div>
          {renderSummary()}
        </section>

        {loaded && !rangeIsEmpty && (
          <div className="rt-folds">
            <Fold label="Compare days">
              <DayTable
                days={t.days}
                selected={t.day}
                today={t.today}
                onSelect={t.selectDay}
                showNavigation={showNavigation}
              />
            </Fold>
            {showNavigation && hasRuns && (
              <Fold label="Navigation health">
                <StatRow metrics={NAV_HEADLINES} day={t.selected} days={t.days} size="sm" />
              </Fold>
            )}
            {dayRunsHref && hasRuns && (
              <Link className="rt-runs-link" to={dayRunsHref(t.day)}>
                <Icon name="list" size={14} />
                See this day's runs in Navigation Analytics
              </Link>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};

RobotTelemetryPanel.propTypes = {
  /** Mongo _id of the robot. Nothing is fetched until it is set. */
  robotId: PropTypes.string,
  /** False keeps the panel mounted without fetching or polling. */
  enabled: PropTypes.bool,
  /** Show recoveries, path efficiency and failure counts (staff view). */
  showNavigation: PropTypes.bool,
  /** Card title; the user portal calls this tab "Activity". */
  heading: PropTypes.string,
  /** Optional (day: "YYYY-MM-DD") => in-app URL listing that day's runs. Staff only. */
  dayRunsHref: PropTypes.func,
};

export default RobotTelemetryPanel;
